import IValidationRuleService from 'features/validation/IValidationRuleService';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import { ChangeEvent, useState } from 'react';
import ValidationRule from 'features/validation/rules/ValidationRule';
import { SuccessValidationResult, ValidationResult } from 'features/validation/models/ValidationResult';
import { Nullable } from 'features/shared/types/nullable';
import { useTranslation } from 'react-i18next';
import { containsKey } from 'features/shared/helpers/objects';
import { produce } from 'immer';
import _ from 'lodash';
import { formatISO, parseISO } from 'date-fns';

//#region TYPES
// TYPES
interface ReactSelectBinding<T extends object> {
  onChange: (selValue: Nullable<T> | Nullable<T[]>) => void;
  name: string;
  options?: T[];
  value?: T | T[] | null;
  isMulti?: boolean;
  getOptionValue?: (option: T) => string;
}

interface FieldBinding<T> {
  name: string;
  value: T;
  onChange: (e: ChangeEvent<any>) => void;
  onBlur?: (e: React.FocusEvent<any>) => void;
}

interface CheckedFieldBinding {
  name: string;
  checked: boolean;
  value: string | undefined;
  onChange: (e: ChangeEvent<any>) => void;
}

interface UncontroledFieldBinding {
  name: string;
  onChange: (e: ChangeEvent<any>) => void;
}

interface DatePickerBinding {
  name: string;
  onChange: (date: Nullable<moment.Moment>) => void
  selectedDate?: Nullable<Date>;
}

export type FormFields = Record<string, any>;
export type FormValidations = Record<string, ValidationRule>;
export type FormErrors = Record<string, string>;
export type ValidationActionType = "add" | "remove";
export type ValueActionType = "add" | "remove";

// FUNCTIONS
export type SetValidationRuleFunction = (key: string, validationRule: Nullable<ValidationRule>, action?: ValidationActionType) => void;
export type SetValidationErrorFunction = (key: string, error: Nullable<string>, action?: ValidationActionType) => void;
export type ValidateFormFieldFunction = (key: string, value: any) => boolean;
export type ValidateFormFunction = () => boolean;
export type ClearValidationErrorsFunction = (keysToClear: string[]) => void;

export type BindFieldOldFunction = (name: string, isCheckedInput?: boolean, checkInputValue?: string) => any;
export type BindFieldFunction = <T>(name: string, clearIfEmpty?: boolean) => FieldBinding<T>;
export type BindUncontrolledFieldFunction = (name: string)  => UncontroledFieldBinding;
export type BindCheckFieldFunction = (name: string, checkInputValue?: string) => CheckedFieldBinding;
export type BindReactSelectFunction = <T extends object>(name: string, key: keyof T, options?: T[], isMulti? : boolean) => ReactSelectBinding<T>;
export type BindDatePickerFunction = (name: string) => DatePickerBinding;

export type SetFormValueFunction = (key: string, value: any, action?: ValueActionType) => void;
export type ClearFormValuesFunction = (formKeys: string[]) => void;
export type IsFilledFormValuesFunction = (formKeys: string[]) => boolean;
export type GetFormValueFunction = <T>(key: string) => T;
export type ClearFormFunction = () => void;

// INPUT
interface FormHookType {
  initialValues?: FormFields;
  initialValidations?: FormValidations;
}

// RETURN TYPE
export type UseFormHookResult = {
  values: FormFields,
  errors: FormErrors,
  bindField: BindFieldOldFunction,
  bindUncontrolledField: BindUncontrolledFieldFunction,
  bindReactSelectField: BindReactSelectFunction,
  bindFieldNew: BindFieldFunction,
  bindCheckField: BindCheckFieldFunction,
  bindDatePickerField: BindDatePickerFunction, 
  setFormValue: SetFormValueFunction,
  getFormValue: GetFormValueFunction,
  isFilledFormValues: IsFilledFormValuesFunction,
  clearFormValues: ClearFormValuesFunction
  valideteForm: ValidateFormFunction,
  validateFormField: ValidateFormFieldFunction,
  setValidationRule: SetValidationRuleFunction,
  setValidationError: SetValidationErrorFunction,
  clearValidationErrors: ClearValidationErrorsFunction,
  clearForm: ClearFormFunction
};

//#endregion TYPES

export function useForm({ initialValues = {}, initialValidations = {} }: FormHookType): UseFormHookResult {
  // DI
  const validationService = useInjection<IValidationRuleService>(IocTypes.ValidationRuleService);

  // TRANSLATIONS
  const tValidations = useTranslation("shared\\validations").t;

  // STATE
  const [values, setValues] = useState(initialValues);
  const [validations, setValidations] = useState<FormValidations>(initialValidations);
  const [errors, setErrors] = useState<FormErrors>({});

  // HANDLERS
  const onChangeHandler = (e: ChangeEvent<any>) => {
    const name = String(e.target.name);
    const value = getValue(e);

    // set value on input change
    setValues(produce((valuesDraft) => {
      _.set(valuesDraft, name, value);
    }));

    // validate value on input change
    validateFieldAndSetError(name, value);  
  }

  const clearOnBlurHandler = (e: React.FocusEvent<any>) => {
    // if value is empty clear it
    // useful in nullable number values (clear instead of setting empty string)
    if (!e.target.value) {
      clearFormValues([e.target.name]);
    }
  }

  // HELPERS
  const getValue = (e: ChangeEvent<any>) => {
    const { name, type, value, files, checked } = e.target;

    switch (type) {
      case "checkbox":
        return processCheckbox(name, value, checked);
      case "file":
        return files;
      default:
        return value;
    }
  }

  const processCheckbox = (name: string, checkBoxvalue: string, checked: boolean) => {
    // checkbox with value = multiple checkbox (value = key)
    if (checkBoxvalue) {
      let checkedKeys:string[] = _.get(values, name, []);
      if (checkedKeys.length > 0) {
        if (checked) {
          return [...checkedKeys, checkBoxvalue];
        } else {
         return checkedKeys.filter(item => item !== checkBoxvalue);
        }
      } else if (checked) {
        return [checkBoxvalue];
      } 
      return [];      
    } 
    // checkbox without value = classic one checkbox
    else {
      return checked;
    }
  }
  
  const isInputChecked = (name: string, checkInputValue?: string): boolean => {  
    const value = _.get(values, name);
    
    if (checkInputValue) {
      if (Array.isArray(value)) {
        return value.some(key => key === checkInputValue);
      } else if (typeof value === "string" || typeof value === "number") {
        return String(value) === checkInputValue;
      }

      return false;
    } else {
      return value ? true : false;
    }   
  }

  const validateField = (name: string, value: any): ValidationResult => {
      // get the validation rule for the field
      const validationRule = validations[name];

      // check if the rules exist, since a field can not have validations
      if (validationRule) {
        const result = validationService.validate<any>(validationRule, value, tValidations);
        return result;
      }      

    return {...SuccessValidationResult};
  }


  const existsValidationRule = (name: string) => {
    return validations[name] ? true : false;
  }
  
  const validateFieldAndSetError = (name: string, value: any): boolean => {
    if (existsValidationRule(name)) {
      let result = validateField(name, value);
      // add error to sate
      if (!result.isValid) {
        setErrors(state => ({
          ...state,
          [name]: result.errorMessage
        }));    
      } 
      // remove error from state
      else if (result.isValid && errors[name]) {
        setErrors((prevErrors) => {
          let newErrors = {...prevErrors}
          delete newErrors[name]
          return newErrors;
        })
      }

      return result.isValid;
    }

    return true;
  }
  
  const getReactSelectValue = <T extends object>(
    name: string,
    key: keyof T,
    options: T[],
    isMulti: boolean
  ): T | T[] | undefined | null => {
    if (options && options.length > 0) {
      if (isMulti) {
        let selectedOptions: T[] = [];
        const valuesMulti: string[] = _.get(values, name);

        if (valuesMulti) {
          for (const multiValue of valuesMulti) {
            const option = options.find((x) => x[key] === multiValue);
            if (option) {
              selectedOptions.push(option);
            }
          }
        }

        return selectedOptions;
      } else {
        const value: string = _.get(values, name);
        const option = options.find((x) => x[key] === value);

        return option ? option : null;
      }
    }

    return null;
  };

  const getReactSelectChangeHandler = <T extends object>(
    selValue: Nullable<T> | Nullable<T[]>,
    name: string,
    key: keyof T,
    isMulti?: boolean
  ) => { 
    if (selValue) {
      let value: T[keyof T] | T[keyof T][];

      if (isMulti && Array.isArray(selValue)) {
        let selectedOptions: T[keyof T][] = [];

        for (const selOption of selValue) {
          if (containsKey(selOption, String(key))) {
            selectedOptions.push(selOption[key]);
          } else {
            console.log(
              `Selected value ${selOption} doesn't contain property '${String(key)}'. Input name: '${name}' (multi-select).`
            );
          }
        }

        value = selectedOptions;
      } else {
        if (!containsKey(selValue, String(key))) {
          console.log(
            `Selected value doesn't contain property '${String(key)}'. Input name: '${name}'.`
          );
        }
        value = (selValue as T)[key];
      }

      setFormValue(name, value);
      validateFieldAndSetError(name, value);
    }
  }
  // OUTPUT FUNCTIONS

  // TODO: nahradit za nove (typove) f-ce
  // DEPRECATED (nepouzivat (nahrazeno typovou verzi a rozdeleno na dve f-ce))
  const bindField = (name: string, isCheckedInput?: boolean, checkInputValue?: string) => {
    if (isCheckedInput) {
      return {
        name: name,
        checked: isInputChecked(name, checkInputValue),
        value: checkInputValue,
        onChange: onChangeHandler
      }
    } else {
      const value = _.get(values, name);
      
      return {
        name: name,
        value: typeof value === "string" || typeof value === "number" ? value : '',
        onChange: onChangeHandler
      }
    }
  }

  const bindFieldNew = <T>(name: string, clearIfEmpty?: boolean): FieldBinding<T> => {
    const value = _.get(values, name);
    const typedValue: T = (
      typeof value === "string" || typeof value === "number" ? value : ""
    ) as T;

    return {
      name: name,
      value: typedValue,
      onChange: onChangeHandler,
      onBlur: clearIfEmpty ? clearOnBlurHandler : undefined
    }
  }

  const bindCheckField = (name: string, checkInputValue?: string): CheckedFieldBinding => {
    return {
      name: name,
      checked: isInputChecked(name, checkInputValue),
      value: checkInputValue,
      onChange: onChangeHandler
    }
  }

  const bindUncontrolledField = (name: string) => {
    return {
      name: name,
      onChange: onChangeHandler
    }
  }

  const bindReactSelectField = <T extends object>(
    name: string,
    key: keyof T,
    options?: T[],
    isMulti?: boolean
  ): ReactSelectBinding<T> => {
    const onChangeHandler = (selValue: Nullable<T> | Nullable<T[]>) =>
      getReactSelectChangeHandler(selValue, name, key, isMulti);

    const getOptionValue = (option: T) => String(option[key]);

    return {
      onChange: onChangeHandler,
      name: name,
      options: options,
      isMulti: isMulti,
      value: getReactSelectValue(name, key, options ?? [], isMulti ?? false),
      getOptionValue: getOptionValue,
    };
  };

  const bindDatePickerFunction = (name: string): DatePickerBinding => {
    const onChangeHandler = (date: Nullable<moment.Moment>) => {
      if (validateFieldAndSetError(name, date) && date !== null) {
        setFormValue(name, formatISO(date.toDate(), { representation: "date"} ));
      } else {
        // support for clearing date picker
        setFormValue(name, null);
      }
    }

    const value = _.get(values, name);
    const formDate = value ? parseISO(value) : null;

    return {
      name: name,
      onChange: onChangeHandler,
      selectedDate: formDate
    }
  }

  const valideteForm = () => {
    let formErrors: FormErrors = {};

    for (const name of Object.keys(validations)) {
      const value = _.get(values, name);
      const validationResult = validateField(name, value);
      if (!validationResult.isValid) {
        formErrors[name] = validationResult.errorMessage;
      }
    }
    
    if (Object.keys(formErrors).length !== 0) {
      setErrors(formErrors);
      return false;
    }
    
    setErrors({});
    return true;
  };

  const clearForm = () => {
    setErrors({});
    setValues(initialValues);
  }
  
  const clearValidationErrors = (keysToClear: string[] = []) => {
    if (Object.keys(errors).length > 0) {
      if (keysToClear.length > 0) {
        let newErrors = {...errors};
        for (const inputKey of keysToClear) {
          if (errors[inputKey]) {
            delete newErrors[inputKey];
          } 
        }
        setErrors(newErrors);
      } else {
        setErrors({});
      }   
    }
  }

  const setFormValue = (key: string, value: any, action: ValueActionType = "add") => {
    switch (action) {
      case "add":
        setValues(produce((valuesDraft) => {
          _.set(valuesDraft, key, value);
        }));

        break;
      case "remove":
        if (_.get(values, key)) {
          setValues((prevValues) => {
            return _.omit(prevValues, key);;
          })  
        }

        break;
    }
  }

  const setValidationRule = (key: string, validationRule: Nullable<ValidationRule>, action: ValidationActionType = "add") => {
    switch (action) {
      case "add":
        if (validationRule) {
          setValidations(state => ({
            ...state,
            [key]: validationRule
          }));   
        }
        break;
      case "remove":
        setValidations((prevValues) => {
          let newValues = {...prevValues}
          delete newValues[key]
          return newValues;
        })
        break;
    }
  }

  const setValidationError = (key: string, error: Nullable<string>, action: ValidationActionType = "add") => {
    switch (action) {
      case "add":
        if (error) {
          setErrors(state => ({
            ...state,
            [key]: tValidations(error)
          }));   
        }
        break;
      case "remove":
        setErrors((prevValues) => {
          let newValues = {...prevValues}
          delete newValues[key]
          return newValues;
        })
        break;
    }
  }

  const validateFormField = (key: string, value: any): boolean => {
    return validateFieldAndSetError(key, value); 
  }

  const clearFormValues = (formKeys: string[]) => { 
    setValues((prevValues) => {
      return _.omit(prevValues, formKeys);;
    });  
  }

  const isFilledFormValues = (formKeys: string[]): boolean => { 
    for (const key of formKeys) {
      if (!_.get(values, key)) {
        return false;
      }
    }

    return true;
  }

  const getFormValue = <T>(key: string): T => { 
    return _.get(values, key);
  }

  // RETURN
  return {
    values: values,
    errors: errors,
    bindField: bindField,
    bindUncontrolledField: bindUncontrolledField,
    bindReactSelectField: bindReactSelectField,
    bindFieldNew: bindFieldNew,
    bindCheckField: bindCheckField,
    bindDatePickerField: bindDatePickerFunction,
    setFormValue: setFormValue,
    getFormValue: getFormValue,
    isFilledFormValues: isFilledFormValues,
    clearFormValues: clearFormValues,
    valideteForm: valideteForm,
    validateFormField: validateFormField,
    setValidationRule: setValidationRule,
    setValidationError: setValidationError,
    clearValidationErrors: clearValidationErrors,
    clearForm: clearForm
  };
}